// ═══════════════════════════════════════════════════════════════════════════
//  THIS PAGE IS ADMIN AND SCRUTINEERS ONLY, BY REGISTRAR RULING.
//  THE SERVER NOW DECIDES THAT — AND THIS FILE STILL ENFORCES NOTHING.
//
//  WHAT CHANGED: `GET /api/results/:ballotId` (@cca/decides-worker,
//  src/routes/results.ts) resolves the reader from a session token, a
//  `Contacts.id` and a `role_grant` row, in that order, and refuses everybody
//  who is not an Admin or a Scrutineer of that sitting IN THAT MODE. The viewer
//  is no longer a claim the browser makes about itself.
//
//  WHAT DID NOT CHANGE, and is the reason this banner stays: this component is
//  a renderer, and a renderer draws whatever it is handed. It does not call that
//  route, it cannot verify that its caller did, and there is no gate inside it
//  to find — see below for why a per-figure gate here would be worse than none.
//  **Whoever assembles a `ResultView` must get it from that route.** The one
//  thing this file no longer does is DEFAULT the viewer: `ResultView.viewer` is
//  required, so a sheet cannot be rendered without somebody having said who
//  asked for it.
//
//  AND THE ROUTE SERVES NO FIGURES TODAY, because there are none to serve:
//  `count_run` records THAT a count ran and never the arithmetic
//  (migrations/0001_initial.sql:499). So the numbers on this screen still come
//  from fixtures, and the deployment condition in the README still governs the
//  day they do not.
//
//  Registrar, 2026-08-16, verbatim: "no numbers are declared on the ballot or
//  vote ever. It is secret. … Nothing that discloses vote numbers, percentages
//  or results should ever be made known to the citizenry during or after the
//  vote. It should be collected and held by the Admin and Scrutineers."
//  Confirmed: there is NO member-facing results screen, at any distance — not
//  during the count, not after the declaration, not in an archive months later.
//  The President declares the outcome to Synod in the room, having checked with
//  the scrutineers, exactly as a manual division works. A member's knowledge of
//  the numbers is the President's sentence, and nothing else.
//
//  So every figure below — each tally, each roll base, each turnout row, each
//  digest, each of the engine's own messages — is restricted material. This
//  page now sits behind a sign-in door (src/App.tsx renders nothing until the
//  worker says who you are), and THAT CHANGES NOTHING HERE: being signed in is
//  not the gate. The gate is the GRANT, checked server-side on the request that
//  asks, and a signed-in caller with no grant for that sitting is refused just
//  as firmly as a stranger. The whole surface is the secret, not some part of it,
//  which is why there is no gate inside these components to find: a per-figure
//  gate on a page that must not be served at all would be theatre, and worse
//  than nothing, because the next person would read it as the control.
//
//  The deployment condition is in README.md ("Owed, and deliberately not done
//  yet") beside the mobile-audit routes and the coverage baseline, because the
//  person who wires this to a worker may never open this file.
// ═══════════════════════════════════════════════════════════════════════════
//
// The results sheet — a DECLARATION SHEET, not a dashboard.
//
// The one-line concept, and every rule below follows from it: the arithmetic is
// the biggest thing on the page, the verdict is a sentence, and the two Houses
// stand side by side with nothing summed between them.
//
// **There is no hero band, and its absence is ratified ground, not taste.**
// migrations/0001_initial.sql:500 — "SO 14.1(k): the President declares. The
// system reports; it never declares." A 72px CARRIED across the top of this page
// is the system declaring. The largest type here is the tally.
//
// Three further page-level rules, each of which prevents a specific harm:
//
//  • **`.page-narrow` (720px) at EVERY width.** This screen does not widen at
//    1024px and up. A wider column invites a second column, a second column
//    invites a summary panel, and a summary panel is the aggregate that SO 8.6
//    says does not exist.
//  • **No `.action-bar`, at any width, and that is load-bearing.** The WCAG 2.2
//    SC 2.4.11 defect on the ballot paper (see the reservation in ballot.css)
//    existed only because a pinned bar obstructed the bottom of the scroll port.
//    No pinned element means the hazard does not arise. If a pinned bar is ever
//    added here, the `scroll-margin-block-end` reservation for every focusable
//    target must arrive in the SAME commit — axe cannot detect 2.4.11, so
//    nothing downstream will catch it.
//  • **A reader sees no controls beyond the provenance disclosure.** The
//    Admin's block renders only under an explicit `viewer === 'admin'` prop from
//    the server's own view of who is asking — never a client-side toggle, and a
//    scrutineer is not the Admin. And SO 8.9's
//    `destruction_authorised_at_utc` is not reachable from this screen at all:
//    it is not a prop, not a flag, and not a field on ResultView.
//
// When a re-count lands (there is no API yet), its result line takes
// `role="status"`, never `role="alert"` — a count finishing is not an alarm, and
// `alert` interrupts whatever a returning officer is reading mid-sentence.

import type { JSX } from 'react';
import { PortalHeader } from '@cca/ui';
import { portalNameForRole } from '../portal-name';
import { IssueList } from './IssueList';
import { ModeBand } from './ModeBand';
import { MotionResult } from './MotionResult';
import { PositionResult } from './PositionResult';
import { Provenance } from './Provenance';
import { RollIntegrityNotice } from './RollIntegrityNotice';
import {
  RESULT_STATE_COPY,
  ballotIssues,
  boxPhrase,
  issuesForItem,
  movedPhrase,
  rollPhrase,
  windowPhrase,
  type CoverageView,
  type ResultView,
} from './view';

export function ResultPage({ view }: { view: ResultView }): JSX.Element {
  // `viewer` is destructured with the rest and NEVER defaulted. The line that
  // used to stand here was `view.viewer ?? 'scrutineer'` — an absent viewer
  // silently became a privileged one, which is the browser deciding the reader
  // by omission. It decides ONE thing on this screen — whether the Admin's own
  // block renders — because everyone in `Viewer` may see every figure on the
  // sheet, and nobody outside it may see the sheet at all. That second half is
  // enforced at GET /api/results/:ballotId, never here; see the banner above.
  const { result, ballot, items, provenance, coverage, rollIntegrity, viewer } = view;

  return (
    <div className="result-app min-dvh">
      {/* The header's name comes from the SAME `viewer` the Admin's block is
          keyed on — the server's word for who asked, never a second guess at
          it — so the sheet cannot be titled for one reader and drawn for
          another. A consequence worth expecting: the three design previews all
          state `viewer: 'scrutineer'`, so an Admin opening one sees the
          Scrutineer's name above it. That is the sheet saying which reader it
          is a preview OF, which is the same thing the preview bar says. */}
      <PortalHeader portalName={portalNameForRole(viewer)} />
      <ModeBand mode={result.mode} />

      <main className="result-main">
        <div className="page-narrow">
          <h1 className="result-title">{ballot.title}</h1>
          <p className="result-state">{RESULT_STATE_COPY[ballot.state]}</p>

          {/* SO 12.1 — quorum is half the members summoned IN EACH House, a
              conjunction like every other rule on this page. One line, because
              the figures belong to the roll and the finding belongs here. */}
          {result.quorum !== null ? (
            <p className="result-quorum">
              {result.quorum.met
                ? 'Quorum was met in each House (SO 12.1).'
                : 'Quorum was NOT met (SO 12.1). Nothing on this page may be declared until that is settled.'}
            </p>
          ) : null}

          {/* The marks-roll check (cca-71hp / cca-5uma) — up here with the other
              facts about the sitting, ABOVE the figures, so a scrutineer meets it
              before reading the count. Silent when the check ran clean; a note
              when it found somebody or could not run (RollIntegrityNotice.tsx).
              Rendered on a fatal count too: the record of who voted is a fact
              about the ballot whether or not the count ran. */}
          <RollIntegrityNotice integrity={rollIntegrity} />

          {/* What the signature covers — the box, the roll, the window — ABOVE
              the figures, so a printed sheet reads as a record of one poll
              before it reads as arithmetic. Rendered on a fatal count too: the
              seal, the roll and the window are facts about the ballot, true
              whether or not the count ran (each phrase is worded to stay true
              there — see view.ts). */}
          <Coverage coverage={coverage} />

          {result.status === 'fatal' ? <FatalNotice view={view} /> : null}

          {/* Issues that name no item are facts about the ROLL or the sitting —
              equally true of every item on the paper, so they are said once,
              here, and not repeated under each one (validate.ts:518). */}
          <IssueList
            issues={ballotIssues(result)}
            heading="About the roll for this sitting"
            level={2}
          />

          {items.map((item, index) =>
            item.kind === 'motion' ? (
              <MotionResult
                key={item.result.itemId}
                view={item}
                itemNumber={index + 1}
                itemCount={items.length}
                issues={issuesForItem(result, item.result.itemId)}
              />
            ) : (
              <PositionResult
                key={item.result.itemId}
                view={item}
                itemNumber={index + 1}
                itemCount={items.length}
                issues={issuesForItem(result, item.result.itemId)}
              />
            ),
          )}

          {/* The one thing `viewer` decides. A scrutineer may see every figure
              on this sheet and is still not the Admin — the audience of the page
              and the authority to act on it are two questions, and this is the
              second one. */}
          {viewer === 'admin' ? <AdminNote /> : null}

          <Provenance provenance={provenance} result={result} />
        </div>
      </main>

      <ModeBand mode={result.mode} repeat />
    </div>
  );
}

/**
 * The box, the roll and the window — what a scrutineer's signature covers, said
 * in sentences (view.ts holds each one and its honest words-for-absence
 * branch). The one structural rule is the extensions list: whenever
 * `extensions` is non-empty the moved statement and every stored sentence
 * render, unconditionally — a signed sheet that reports only the close that
 * finally stood, with no word that it moved, is the omission this whole block
 * exists to close. The stored `reason` is prose off the ballot's own chain and
 * reaches the DOM as the text child of an `<li>`, never as markup.
 */
function Coverage({ coverage }: { coverage: CoverageView }): JSX.Element {
  return (
    <section className="card result-coverage" aria-labelledby="result-coverage-heading">
      <h2 className="result-subheading" id="result-coverage-heading">
        What this sheet covers
      </h2>
      <p className="result-coverage-text">{boxPhrase(coverage)}</p>
      <p className="result-coverage-text">{rollPhrase(coverage)}</p>
      <p className="result-coverage-text">{windowPhrase(coverage)}</p>
      {coverage.extensions.length > 0 ? (
        <>
          <p className="result-coverage-text">{movedPhrase(coverage.extensions.length)}</p>
          <ul className="result-coverage-extensions">
            {coverage.extensions.map((extension, index) => (
              <li key={`${extension.occurredAtNz}-${String(index)}`}>
                {extension.occurredAtNz} — {extension.reason ?? 'no note was recorded'}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}

/** `status: 'fatal'` — the input was malformed and `items` is empty. The engine
 *  never throws, so this is the only failure shape there is, and a page that
 *  rendered an empty list for it would look like a ballot on which nothing
 *  happened. */
function FatalNotice({ view }: { view: ResultView }): JSX.Element {
  return (
    <section className="card result-withheld">
      <h2 className="result-withheld-title">This count did not run.</h2>
      <p className="result-withheld-text">
        The ballot could not be counted, so there are no tallies to report and nothing here may be
        declared. The papers are untouched; what follows is what stopped the count.
      </p>
      <IssueList issues={view.result.fatal} heading="What stopped the count" level={3} />
    </section>
  );
}

/**
 * The Admin's block — `viewer === 'admin'`, which is the role `role_grant`
 * spells that way and the route sends by that name. It carries no controls, and
 * that is not an omission waiting to be filled: everything it names is an act
 * with a record attached — settling a roll, recording a fresh vote or a draw —
 * and none of them is a button on a reporting screen.
 */
function AdminNote(): JSX.Element {
  return (
    <section className="card result-admin" aria-labelledby="admin-note">
      <h2 className="result-subheading" id="admin-note">
        For the Admin
      </h2>
      <p className="result-admin-text">
        This screen reports a count; it does not change one. Settling a contradicted roll, recording
        a fresh vote or the Registrar&rsquo;s draw under Standing Order 15.13, and running the count
        again all happen elsewhere and are recorded as they happen. A new count never replaces this
        one — both are kept, so the sequence stays readable to a scrutineer.
      </p>
    </section>
  );
}
